import { logger } from '@lib/logger'
import { DateTime, IANAZone } from 'luxon'
import {
  DateDisplayFormat,
  getDateInputPlaceholderByVenueDateDisplayFormat,
  resolveVenueDateDisplayFormat,
} from 'shared'
import { InputOption } from 'types/ui'
import { DayDetail } from 'types/ui/calendar'

export {
  resolveVenueDateDisplayFormat,
  getDateInputPlaceholderByVenueDateDisplayFormat,
}

export const CALENDAR_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const MONTH_OPTIONS: InputOption[] = CALENDAR_MONTHS.map(
  (month, index) => ({
    name: month,
    id: index.toString(),
  })
)

export const CALENDAR_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export function getLuxonDateFormatByVenueDateDisplayFormat(
  dateDisplayFormat?: string | null
): string {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'dd/MM/yyyy'
    : 'MM/dd/yyyy'
}

export function getDatePickerDisplayFormatByVenueDateDisplayFormat(
  dateDisplayFormat?: string | null
): string {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'EEE, d/M/yyyy'
    : 'EEE, M/d/yyyy'
}

export function getLongDateFormatByVenueDateDisplayFormat(
  dateDisplayFormat?: string | null
): string {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'cccc, d LLLL yyyy'
    : 'cccc, LLLL d, yyyy'
}

export const WEEKDAY_OPTIONS: InputOption[] = CALENDAR_WEEKDAYS.map(
  (weekday, index) => ({
    name: weekday,
    id: index.toString(),
  })
)

export function getBeginningOfDate(dt?: Date | number | string | null) {
  const date = !dt ? new Date() : new Date(dt)

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  )
}

function getVenueZonedDate(
  dt?: Date | number | string | null,
  timezone?: string
) {
  const inputDate = !dt ? new Date() : new Date(dt)
  const baseDate = DateTime.fromJSDate(inputDate)
  const timezoneDate = timezone ? baseDate.setZone(timezone) : baseDate
  return timezoneDate.isValid ? timezoneDate : baseDate
}

/**
 * Venue calendar day at start-of-day in that zone.
 * Unlike {@link getBusinessDate}, does not apply the nightlife 6AM cutoff.
 */
export function getCalendarDate(
  dt?: Date | number | string | null,
  options: { timezone?: string } = {}
) {
  const { timezone } = options
  const calendarDate = getVenueZonedDate(dt, timezone)

  // Start-of-day in `calendarDate`'s zone (venue TZ when provided). Using
  // `new Date(y, m, d)` instead used the *host* local zone (SSR is often UTC),
  // so the same wall calendar day serialized to ISO looked like "yesterday" for
  // US users when parsed back with local getters / formatters.
  return calendarDate.startOf('day').toJSDate()
}

export function getBusinessDate(
  dt?: Date | number | string | null,
  options: { timezone?: string; cutoffHour?: number } = {}
) {
  const { timezone, cutoffHour = 6 } = options
  const businessReferenceDate = getVenueZonedDate(dt, timezone)
  const businessDate =
    businessReferenceDate.hour < cutoffHour
      ? businessReferenceDate.minus({ days: 1 })
      : businessReferenceDate

  // Start-of-day in `businessDate`'s zone (venue TZ when provided). Using
  // `new Date(y, m, d)` instead used the *host* local zone (SSR is often UTC),
  // so the same wall calendar day serialized to ISO looked like "yesterday" for
  // US users when parsed back with local getters / formatters.
  return businessDate.startOf('day').toJSDate()
}

/** Inventory calendar bounds using one IANA zone (e.g. venue/event TZ), stable across SSR and client. */
export function getInventoryCalendarBoundsMs(
  nowMs: number,
  purchaseWindowDays: number,
  timeZone?: string | null
): [number, number] {
  const zone = timeZone && IANAZone.isValidZone(timeZone) ? timeZone : 'UTC'
  const now = DateTime.fromMillis(nowMs, { zone })
  const hour = now.hour
  const businessStart = now.minus({ days: hour < 6 ? 1 : 0 }).startOf('day')
  const calendarAnchor = now.startOf('day')
  const end = calendarAnchor.plus({ days: purchaseWindowDays })
  return [businessStart.toMillis(), end.toMillis()]
}

/**
 * Venue event list / purchase window bounds in one IANA zone (6am business-day cutoff,
 * `startOf('day')` in that zone). Avoids `utcRange` + `Date` local getters so SSR and
 * client stay aligned with `getInventoryCalendarBoundsMs`-style Luxon math.
 */
export function getVenuePurchaseRangeMs(
  nowMs: number,
  purchaseWindowDays: number,
  timeZone?: string | null
): [number, number] {
  const zone = timeZone && IANAZone.isValidZone(timeZone) ? timeZone : 'UTC'
  const now = DateTime.fromMillis(nowMs, { zone })
  const hour = now.hour
  const rangeStart = now.minus({ days: hour < 6 ? 1 : 0 }).startOf('day')
  const rangeEnd = rangeStart.plus({ days: purchaseWindowDays })
  return [rangeStart.toMillis(), rangeEnd.toMillis()]
}

/** Whether the event has ended, comparing wall-clock instants (same on server and client for a given nowMs). */
function getVenueEventEndTime(
  dateStart: string | number,
  dateEnd: string | number,
  options: { timezone?: string } = {}
): number | null {
  const startParsed = new Date(dateStart)
  const endParsed = new Date(dateEnd)
  if (
    Number.isNaN(startParsed.valueOf()) ||
    Number.isNaN(endParsed.valueOf())
  ) {
    return null
  }

  const timezone = options.timezone
  if (timezone) {
    const startISO = utcToISO(dateStart, { timezone })
    const endISO = utcToISO(dateEnd, { timezone })
    const startMs = startISO ? Date.parse(startISO) : Number.NaN
    let endMs = endISO ? Date.parse(endISO) : Number.NaN
    if (!Number.isNaN(startMs) && !Number.isNaN(endMs)) {
      if (endMs <= startMs) {
        endMs += 24 * 60 * 60 * 1000
      }
      return endMs
    }
  }

  const startMs = startParsed.valueOf()
  let endMs = endParsed.valueOf()
  if (endMs <= startMs) {
    endMs += 24 * 60 * 60 * 1000
  }
  return endMs
}
export function isVenueEventPassedByInstant(
  nowMs: number,
  dateStart: string | number,
  dateEnd: string | number,
  options: { timezone?: string } = {}
): boolean {
  const eventEndMs = getVenueEventEndTime(dateStart, dateEnd, options)
  if (eventEndMs === null) return false
  return nowMs > eventEndMs
}

export function utcToISO(
  utc?: number | string | undefined | null,
  options: { startOfDay?: boolean; timezone?: string } = {}
) {
  if (!utc) return ''
  const { startOfDay = false, timezone } = options

  const dt = new Date(utc)
  const utcYear = dt.getUTCFullYear()
  const utcMonth = dt.getUTCMonth()
  const utcDate = dt.getUTCDate()
  const utcHours = startOfDay ? 0 : dt.getUTCHours()
  const utcMinutes = startOfDay ? 0 : dt.getUTCMinutes()
  const utcSeconds = startOfDay ? 0 : dt.getUTCSeconds()
  const utcMilliseconds = startOfDay ? 0 : dt.getUTCMilliseconds()

  if (timezone) {
    // Event dateStart/dateEnd values are "floating UTC" (wall-clock values).
    // Interpret UTC components in the venue timezone to avoid browser-local shifts.
    const zonedDate = DateTime.fromObject(
      {
        year: utcYear,
        month: utcMonth + 1,
        day: utcDate,
        hour: utcHours,
        minute: utcMinutes,
        second: utcSeconds,
        millisecond: utcMilliseconds,
      },
      { zone: timezone }
    )
    if (zonedDate.isValid) return zonedDate.toUTC().toISO() ?? ''
  }

  const utcLocalDate = new Date(
    utcYear,
    utcMonth,
    utcDate,
    utcHours,
    utcMinutes,
    utcSeconds,
    utcMilliseconds
  )

  return utcLocalDate.toISOString()
}

/**
 * Stable calendar-day key for comparing inventory browse dates to event diary dates.
 * Uses the host-local calendar components of the parsed instant (same wall-clock day
 * semantics as {@link isoToUTC} with `startOfDay: true`), avoiding mixed UTC/local field bugs.
 */
export function getInventoryBrowseDiaryDayKey(
  input: string | number | Date
): string | undefined {
  const dt =
    typeof input === 'number'
      ? new Date(input)
      : input instanceof Date
        ? input
        : new Date(input)
  const ms = dt.valueOf()
  if (Number.isNaN(ms)) return undefined

  const y = dt.getFullYear()
  const m = dt.getMonth() + 1
  const d = dt.getDate()
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function isoToUTC(
  iso: string | undefined | null,
  options: { startOfDay: boolean } = { startOfDay: false }
) {
  if (!iso) return
  const { startOfDay } = options

  const dt = new Date(iso)
  return Date.UTC(
    dt.getFullYear(),
    dt.getMonth(),
    dt.getDate(),
    startOfDay ? 0 : dt.getUTCHours(),
    startOfDay ? 0 : dt.getUTCMinutes(),
    startOfDay ? 0 : dt.getUTCSeconds()
  )
}

export function utcRange(
  start: number | string,
  {
    days = 0,
    months = 0,
    years = 0,
  }: { days?: number; months?: number; years?: number }
) {
  const beginningDate = new Date(start)
  const dateStart = Date.UTC(
    beginningDate.getFullYear(),
    beginningDate.getMonth(),
    beginningDate.getDate(),
    0,
    0,
    0
  )
  const dateEnd = Date.UTC(
    beginningDate.getFullYear() + years,
    beginningDate.getMonth() + months,
    beginningDate.getDate() + days,
    0,
    0,
    0
  )

  return [dateStart, dateEnd]
}

export function getOffsetUTC(date?: string | number | Date | null) {
  if (!date) return null
  try {
    const dt = new Date(date)
    return Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 6).toString()
  } catch {
    return null
  }
}

export function getTimeLabel(value?: {
  hour?: number | undefined
  minute?: number | undefined
  meridiem?: 'AM' | 'PM' | undefined
}) {
  if (!value) return undefined
  const { hour, minute, meridiem } = value

  if (hour === undefined || minute === undefined || meridiem === undefined)
    return ''
  const minuteString = String(minute)
  return `${hour === 0 && meridiem === 'AM' ? 12 : hour}:${
    minuteString.length < 2 ? `0${minuteString}` : minuteString
  } ${meridiem}`
}

// TODO: try to replace luxon format with Intl.DateTimeFormat
export function withDateFormat(
  date: Date | number | string,
  formatString: string,
  timezone?: string
) {
  let dt =
    typeof date === 'number'
      ? DateTime.fromMillis(date).setZone(timezone)
      : typeof date === 'string'
        ? DateTime.fromISO(date).setZone(timezone)
        : DateTime.fromJSDate(date).setZone(timezone)
  return dt.toFormat(formatString)
}

export function getDaysInTheMonth(year: number, month: number) {
  return 40 - new Date(year, month, 40).getDate()
}

export function getCalendarDays(year: number, month: number) {
  try {
    const prevMonth = month === 0 ? 11 : month - 1
    const prevMonthYear = prevMonth === 11 ? year - 1 : year
    const prevMonthDayCount = getDaysInTheMonth(prevMonthYear, prevMonth)

    const jsFirstDate = new Date(year, month)
    const numberOfDays = getDaysInTheMonth(year, month)

    const firstDateWeekday = jsFirstDate.getDay()
    const calendarDayCells: DayDetail[] = Array(firstDateWeekday)

    let prevMonthWeekday = firstDateWeekday - 1
    let prevMonthDatesAdded = 0

    // fill in the cells for the previous month in the first row that will be disabled
    while (prevMonthWeekday >= 0) {
      const prevDateNumber = prevMonthDayCount - prevMonthDatesAdded
      const date = new Date(prevMonthYear, prevMonth, prevDateNumber, 0, 0, 0)
      calendarDayCells[prevMonthWeekday] = {
        date,
        rfc: rfcFormat(date.valueOf()),
        year: prevMonthYear,
        month: prevMonth,
        day: prevDateNumber,
        weekday: prevMonthWeekday,
        timestamp: date.valueOf(),
      }
      ++prevMonthDatesAdded
      --prevMonthWeekday
    }

    // fill in the rest of the month days up until the saturday of the last row, extending into the next month if necessary
    let dayOfMonth = 1
    while (
      dayOfMonth <= numberOfDays ||
      (dayOfMonth > numberOfDays && calendarDayCells.at(-1)?.weekday !== 6)
    ) {
      const nextDate = new Date(year, month, dayOfMonth, 0, 0, 0)
      calendarDayCells.push({
        date: nextDate,
        rfc: rfcFormat(nextDate.valueOf()),
        year: nextDate.getFullYear(),
        month: nextDate.getMonth(),
        day: nextDate.getDate(),
        weekday: nextDate.getDay(),
        timestamp: nextDate.valueOf(),
      })
      ++dayOfMonth
    }

    return calendarDayCells
  } catch (e) {
    logger.error('getCalendarDays(): Error', (e as Error).message || e)
    return []
  }
}

export function getDateFromNow({
  years = 0,
  months = 0,
  days = 0,
}: {
  years?: number
  months?: number
  days?: number
} = {}) {
  const nowDate = new Date()
  const nowYears = nowDate.getFullYear()
  const nowMonths = nowDate.getMonth()
  const nowDays = nowDate.getDate()

  const dateFromNow = new Date(
    nowYears + years,
    nowMonths + months,
    nowDays + days
  )
  return dateFromNow
}

export function getUTCFromNow({
  years = 0,
  months = 0,
  days = 0,
}: {
  years?: number
  months?: number
  days?: number
} = {}) {
  const nowDate = new Date()
  const nowYears = nowDate.getFullYear()
  const nowMonths = nowDate.getMonth()
  const nowDays = nowDate.getDate()

  return new Date(
    nowYears + years,
    nowMonths + months,
    nowDays + days
  ).valueOf()
}

export function minuteNumberToString(val: number | null | undefined) {
  if (val === null || val === undefined) return ''
  return `${val < 10 ? '0' : ''}${val}`
}

export function addToDate(
  date: Date | string | number | null | undefined,
  units: {
    days?: number
    months?: number
    years?: number
    hours?: number
    minutes?: number
    seconds?: number
  }
) {
  if (!date) return

  const dt = new Date(date)
  const days = dt.getDate() + (units.days ?? 0)
  const months = dt.getMonth() + (units.months ?? 0)
  const years = dt.getFullYear() + (units.years ?? 0)
  const hours = dt.getHours() + (units.hours ?? 0)
  const minutes = dt.getMinutes() + (units.minutes ?? 0)
  const seconds = dt.getSeconds() + (units.seconds ?? 0)

  return new Date(years, months, days, hours, minutes, seconds)
}

export function rfcFormat(value?: number | string | null | undefined) {
  const val = new Date(value ?? Date.now())
  const yearString = String(val.getFullYear())
  const monthString = String(val.getMonth())
  const dayString = String(val.getDate())
  const pad = (value: string) => (value.length < 2 ? `0${value}` : value)
  return `${yearString}-${pad(monthString)}-${pad(dayString)}`
}

export function getYearFrom(value: string | number) {
  return new Date(value).getFullYear()
}

export function getMonthFrom(value: string | number) {
  return new Date(value).getMonth() + 1 // Month value is zero indexed on the Date object
}

export function getDayFrom(value: string | number) {
  return new Date(value).getDate()
}
